import {
	Column,
	Entity,
	Index,
	JoinColumn,
	ManyToOne,
	OneToMany,
} from 'typeorm';
import { BaseEntity } from './base.entity';
import { ClientAddressItemEntity } from './client-address-item.entity';
import type { ClientLicenseEntity } from './client-license.entity';
import { ClientEntity } from './client.entity';
import { InvoiceEntity } from './invoice.entity';
import { OrderEntity } from './order.entity';

@Index('client_address__fk_client__id_fk', ['fkClientId'], {})
@Index('client_address_externalId_uindex', ['deletedUserId', 'externalId'], {
	unique: true,
})
// PD-17414: imports look a record up by externalId alone (upserts sent with
// `deleted: true` drop the _deleted_userId filter), which the composite index
// above cannot serve, so every lookup read the whole table.
@Index('client_address__externalId', ['externalId'], {})
@Entity('client_address')
export class ClientAddressEntity extends BaseEntity {
	@Column('varchar', { name: 'externalId', nullable: true, length: 64 })
	public externalId!: string | null;

	@Column('varchar', { name: 'externalId2', nullable: true, length: 64 })
	public externalId2!: string | null;

	@Column('varchar', { name: 'address1', nullable: true, length: 255 })
	public address1!: string | null;

	@Column('varchar', { name: 'address2', nullable: true, length: 255 })
	public address2!: string | null;

	@Column('varchar', { name: 'city', nullable: true, length: 255 })
	public city!: string | null;

	@Column('varchar', { name: 'state', nullable: true, length: 255 })
	public state!: string | null;

	@Column('varchar', { name: 'zip', nullable: true, length: 255 })
	public zip!: string | null;

	@Column('decimal', {
		name: 'latitude',
		precision: 10,
		scale: 8,
		nullable: true,
	})
	public latitude!: number | null;

	@Column('decimal', {
		name: 'longitude',
		precision: 11,
		scale: 8,
		nullable: true,
	})
	public longitude!: number | null;

	@Column('varchar', { name: 'billtoOrShipto', nullable: true, length: 255 })
	public billtoOrShipto!: string | null;

	@Column('int', { name: '_fk_client__id', unsigned: true })
	public fkClientId!: number;

	@Column({
		type: 'boolean',
		name: 'isRxAllowed',
		nullable: true,
		width: 1,
		default: () => "'0'",
	})
	public isRxAllowed!: boolean | null;

	@Column({
		type: 'boolean',
		name: 'isSchedule2Allowed',
		nullable: true,
		width: 1,
		default: () => "'0'",
	})
	public isSchedule2Allowed!: boolean | null;

	@Column({
		type: 'boolean',
		name: 'isSchedule2NAllowed',
		nullable: true,
		width: 1,
		default: () => "'0'",
	})
	public isSchedule2NAllowed!: boolean | null;

	@Column({
		type: 'boolean',
		name: 'isSchedule3Allowed',
		nullable: true,
		width: 1,
		default: () => "'0'",
	})
	public isSchedule3Allowed!: boolean | null;

	@Column({
		type: 'boolean',
		name: 'isSchedule3NAllowed',
		nullable: true,
		width: 1,
		default: () => "'0'",
	})
	public isSchedule3NAllowed!: boolean | null;

	@Column({
		type: 'boolean',
		name: 'isSchedule4Allowed',
		nullable: true,
		width: 1,
		default: () => "'0'",
	})
	public isSchedule4Allowed!: boolean | null;

	@Column({
		type: 'boolean',
		name: 'isSchedule5Allowed',
		nullable: true,
		width: 1,
		default: () => "'0'",
	})
	public isSchedule5Allowed!: boolean | null;

	@OneToMany(() => ClientEntity, (client) => client.fkClientAddressIdBilling2)
	public clients!: Array<ClientEntity>;

	@OneToMany(() => ClientEntity, (client) => client.fkClientAddressIdShipping2)
	public clients2!: Array<ClientEntity>;

	@ManyToOne(() => ClientEntity, (client) => client.clientAddresses, {
		onDelete: 'RESTRICT',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: '_fk_client__id', referencedColumnName: 'id' }])
	public fkClient!: ClientEntity;

	@OneToMany(
		() => ClientAddressItemEntity,
		(clientAddressItem) => clientAddressItem.fkClientAddress,
	)
	public clientAddressItems!: Array<ClientAddressItemEntity>;

	@OneToMany('ClientLicenseEntity', 'fkClientAddress')
	public clientLicenses!: Array<ClientLicenseEntity>;

	@OneToMany(
		() => InvoiceEntity,
		(invoice) => invoice.fkClientAddressIdBilling2,
	)
	public invoices!: Array<InvoiceEntity>;

	@OneToMany(() => OrderEntity, (order) => order.fkClientAddressIdBilling2)
	public orders!: Array<OrderEntity>;

	@OneToMany(() => OrderEntity, (order) => order.fkClientAddressIdShipping2)
	public orders2!: Array<OrderEntity>;
}
